import {
  useExtractInvoiceCharges,
  useSite,
  useSiteFileUpload,
} from "@inrange/building-manager-api-client";
import {
  NullableAllowStringForNumbers,
  Site,
  SiteFile,
} from "@inrange/building-manager-api-client/models-site";
import { Modal, ModalView } from "@inrange/theme-components";
import { currencySymbol } from "@inrange/theme-components/formatting";
import { AxiosError } from "axios";
import { ChangeEvent, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row, Stack } from "react-bootstrap";
import { epochSecondsToDate, epochSecondsToDateInTz } from "./utils";

const EditFileModal = ({
  site,
  siteFile,
  onClose,
}: {
  site: Site;
  siteFile: SiteFile;
  onClose: () => void;
}) => {
  const [file, setFile] =
    useState<NullableAllowStringForNumbers<SiteFile>>(siteFile);
  const [error, setError] = useState<string | null>(null);
  const [isExistingFile, _setIsExistingFile] = useState<boolean>(
    !!file.fileVersionId
  );
  const isSymbolicFile = !!siteFile.isQuery;
  const [saveDisabled, setSaveDisabled] = useState(
    file.fileVersionId ? false : true
  );
  const { updateSite } = useSite({
    siteId: site.id,
    app: "admin",
  });
  const { uploadSiteFile } = useSiteFileUpload(site.id);
  const { extractCharges, isLoading: isExtractingCharges } =
    useExtractInvoiceCharges();

  const [invoiceLineItems, setInvoiceLineItems] = useState<
    Array<{
      description: string;
      amount: string;
      type: "charge" | "consumption_adjustment";
    }>
  >(() => {
    if (!siteFile.invoiceOtherCharges) return [];
    return siteFile.invoiceOtherCharges.map((item) => ({
      description: String(item.description || ""),
      amount: String(item.amount ?? ""),
      type: item.type ?? "charge",
    }));
  });

  useEffect(() => {
    const items = invoiceLineItems
      .filter((item) => item.description.trim() || parseFloat(item.amount))
      .map((item) => ({
        description: item.description.trim(),
        amount: parseFloat(item.amount) || 0,
        type: item.type,
      }));
    setFile((prev) => ({
      ...prev,
      invoiceOtherCharges: items.length > 0 ? items : undefined,
    }));
  }, [invoiceLineItems]);

  const onUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];

    if (selectedFile) {
      uploadSiteFile.mutate(
        {
          file: selectedFile,
          siteFile: {
            fileGuid: file.fileGuid,
            fileType: file.fileType || "",
            fileName: selectedFile.name,
          },
        },
        {
          onSuccess: (data) => {
            setFile({
              ...file,
              fileType: data.fileType,
              fileName: data.fileName,
              fileGuid: data.fileGuid,
              fileVersionId: null,
            });
            setSaveDisabled(false);
          },
          onError: (error) => {
            const axiosError = error as AxiosError;
            const errorMessage =
              axiosError.response?.data?.["message"] || axiosError.message;
            setError(`Error uploading file: ${errorMessage}`);
          },
        }
      );
    }
  };

  const uploadInProgress = uploadSiteFile.isLoading;
  const isFileSelected = !!file.fileName;

  return (
    <Modal>
      <ModalView
        title={`${isExistingFile ? "Edit" : "Add"} file`}
        fontWeight="800"
        fontSize="18px"
        titlePadding="0 0 10px 0"
        width="60%"
      >
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Select
                value={file.fileType!}
                onChange={(e) => {
                  const newType = e.target.value;
                  setFile({
                    ...file,
                    fileType: newType,
                    date:
                      newType === "siteDocument" && !file.date
                        ? Math.floor(Date.now() / 1000)
                        : file.date,
                  });
                }}
                data-testid={"site-file-type"}
                disabled={isFileSelected}
              >
                <option value={"reading"}>Reading</option>
                <option value={"invoice"}>Invoice</option>
                <option value={"statement"}>Statement</option>
                <option value={"siteDocument"}>Site Document</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        {isSymbolicFile ? (
          <Row className="align-items-end">
            <Col>
              <Form.Group controlId="fileDescription">
                <Form.Label>Filename</Form.Label>
                <Form.Control
                  type="text"
                  data-testid={`${file.fileName}-name`}
                  value={file.fileName || ""}
                  disabled
                />
              </Form.Group>
              <div className="text-muted mt-1" style={{ fontSize: "0.85em" }}>
                This is a symbolic file — its content is generated dynamically
                from the database.
              </div>
            </Col>
          </Row>
        ) : (
          <Row className="align-items-end">
            {(uploadInProgress || (!uploadInProgress && !!file.fileName)) && (
              <Col>
                {uploadInProgress && <div className="mb-1">(Uploading...)</div>}
                {!uploadInProgress && !!file.fileName && (
                  <Form.Group controlId="fileDescription">
                    <Form.Label>
                      Filename
                      {!!file.fileName && !file.fileVersionId && (
                        <>
                          {" "}
                          <b>(unsaved)</b>
                        </>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      data-testid={`${file.fileName}-name`}
                      value={file.fileName || ""}
                      onChange={(e) =>
                        setFile({
                          ...file,
                          fileName: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                )}
              </Col>
            )}
            <Col md="auto">
              <Form.Group style={{ display: "flex", flexDirection: "column" }}>
                <Form.Control
                  type="file"
                  id="site-file-upload"
                  style={{ display: "none" }}
                  accept={
                    file.fileType === "siteDocument"
                      ? "*"
                      : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf"
                  }
                  onChange={(e) =>
                    onUpload(e as unknown as ChangeEvent<HTMLInputElement>)
                  }
                />
                <Stack direction="horizontal" gap={3}>
                  <label
                    htmlFor="site-file-upload"
                    className="btn btn-success"
                    data-testid={"site-file-upload"}
                  >
                    Upload {!uploadInProgress && !!file.fileName ? "new " : ""}
                    file
                  </label>
                </Stack>
              </Form.Group>
            </Col>
          </Row>
        )}
        {isFileSelected && (
          <>
            <Row className="mt-3">
              <Col md={6}>
                <Form.Group className="mb-3" controlId="fileDescription">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    data-testid={`${file.fileName}-description`}
                    value={file.description || ""}
                    onChange={(e) =>
                      setFile({
                        ...file,
                        description: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
              {(file.fileType == "reading" || file.fileType == "siteDocument") && (
                <Col md="3">
                  <Form.Group className="mb-3" controlId="fileDate">
                    <Form.Label>
                      {file.fileType == "reading"
                        ? "Uploaded Date"
                        : "Document Date"}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      onClick={(e) => {
                        const element = e.target;
                        if ("showPicker" in HTMLInputElement.prototype) {
                          // showPicker() is supported. Chrome 99+ and Safari 16+
                          // see https://caniuse.com/?search=showPicker
                          (element as HTMLInputElement).showPicker();
                        }
                      }}
                      data-testid={`${file.fileName}-date`}
                      value={epochSecondsToDate(file.date)}
                      onChange={(e) => {
                        const date = new Date(e.target.value);
                        const invalidDate = isNaN(date as unknown as number);
                        const epochSeconds = invalidDate
                          ? null
                          : date.getTime() / 1000; // if the date was cleared, set to null, otherwise getTime returns milliseconds, but we want seconds
                        setFile({
                          ...file,
                          date: epochSeconds,
                        });
                      }}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Row>
              {file.fileType == "invoice" && (
                <>
                  <Col md="3">
                    <Form.Group className="mb-3" controlId="fileInvoiceNumber">
                      <Form.Label>Invoice Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={file.invoiceNumber || ""}
                        onChange={(e) =>
                          setFile({
                            ...file,
                            invoiceNumber: e.target.value,
                          })
                        }
                        data-testid={`${file.fileName}-invoice-number`}
                      />
                    </Form.Group>
                  </Col>
                  <Col md="3">
                    <Form.Group className="mb-3" controlId="fileInvoiceAmount">
                      <Form.Label>Invoice Amount</Form.Label>
                      <Form.Control
                        type="text"
                        value={file.invoiceAmount || ""}
                        onChange={(e) =>
                          setFile({
                            ...file,
                            invoiceAmount: e.target.value,
                          })
                        }
                        data-testid={`${file.fileName}-invoice-amount`}
                      />
                    </Form.Group>
                  </Col>
                  <Col md="3">
                    <Form.Group
                      className="mb-3"
                      controlId="fileInvoiceIssueDate"
                    >
                      <Form.Label>Issue Date</Form.Label>
                      <Form.Control
                        type="date"
                        onClick={(e) => {
                          const element = e.target;
                          if ("showPicker" in HTMLInputElement.prototype) {
                            // showPicker() is supported. Chrome 99+ and Safari 16+
                            // see https://caniuse.com/?search=showPicker
                            (element as HTMLInputElement).showPicker();
                          }
                        }}
                        value={epochSecondsToDateInTz(
                          file.issueDate,
                          site.timeZone
                        )}
                        onChange={(e) => {
                          const date = new Date(e.target.value);
                          const invalidDate = isNaN(date as unknown as number);
                          const epochSeconds = invalidDate
                            ? null
                            : date.getTime() / 1000; // if the date was cleared, set to null, otherwise getTime returns milliseconds, but we want seconds
                          setFile({
                            ...file,
                            issueDate: epochSeconds,
                          });
                        }}
                        data-testid={`${file.fileName}-issue-date`}
                      />
                    </Form.Group>
                  </Col>
                  <Col md="3">
                    <Form.Group className="mb-3" controlId="fileInvoiceDueDate">
                      <Form.Label>Due Date</Form.Label>
                      <Form.Control
                        type="date"
                        onClick={(e) => {
                          const element = e.target;
                          if ("showPicker" in HTMLInputElement.prototype) {
                            // showPicker() is supported. Chrome 99+ and Safari 16+
                            // see https://caniuse.com/?search=showPicker
                            (element as HTMLInputElement).showPicker();
                          }
                        }}
                        value={epochSecondsToDateInTz(
                          file.invoiceDueDate,
                          site.timeZone
                        )}
                        onChange={(e) => {
                          const date = new Date(e.target.value);
                          const invalidDate = isNaN(date as unknown as number);
                          const epochSeconds = invalidDate
                            ? null
                            : date.getTime() / 1000; // if the date was cleared, set to null, otherwise getTime returns milliseconds, but we want seconds
                          setFile({
                            ...file,
                            invoiceDueDate: epochSeconds,
                          });
                        }}
                        data-testid={`${file.fileName}-due-date`}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
              {file.fileType === "invoice" && (
                <Col md="12" className="mt-2">
                  <Form.Label>Other charges and adjustments</Form.Label>
                  {invoiceLineItems.map((item, index) => (
                    <div
                      key={index}
                      className="d-flex gap-2 mb-1 align-items-center"
                    >
                      <Form.Control
                        type="text"
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => {
                          const updated = [...invoiceLineItems];
                          updated[index] = {
                            ...updated[index],
                            description: e.target.value,
                          };
                          setInvoiceLineItems(updated);
                        }}
                        size="sm"
                      />
                      <Form.Control
                        type="text"
                        placeholder={
                          item.type === "consumption_adjustment"
                            ? "kWh"
                            : "Amount"
                        }
                        value={item.amount}
                        onChange={(e) => {
                          const updated = [...invoiceLineItems];
                          updated[index] = {
                            ...updated[index],
                            amount: e.target.value,
                          };
                          setInvoiceLineItems(updated);
                        }}
                        style={{ width: "120px" }}
                        size="sm"
                      />
                      <Form.Select
                        value={item.type}
                        onChange={(e) => {
                          const updated = [...invoiceLineItems];
                          updated[index] = {
                            ...updated[index],
                            type: e.target.value as
                              | "charge"
                              | "consumption_adjustment",
                          };
                          setInvoiceLineItems(updated);
                        }}
                        style={{ width: "180px" }}
                        size="sm"
                      >
                        <option value="charge">
                          Charge ({currencySymbol(site.currencyCode)})
                        </option>
                        <option value="consumption_adjustment">
                          Consumption adj. (kWh)
                        </option>
                      </Form.Select>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() =>
                          setInvoiceLineItems(
                            invoiceLineItems.filter((_, i) => i !== index)
                          )
                        }
                      >
                        x
                      </Button>
                    </div>
                  ))}
                  <div className="d-flex gap-3 align-items-center mb-2">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() =>
                        setInvoiceLineItems([
                          ...invoiceLineItems,
                          { description: "", amount: "", type: "charge" },
                        ])
                      }
                    >
                      + Add line item
                    </Button>
                    {isExistingFile &&
                      file.fileName?.toLowerCase().endsWith(".pdf") && (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0"
                          disabled={isExtractingCharges}
                          onClick={async () => {
                            try {
                              const items = await extractCharges(
                                site.id,
                                siteFile.fileGuid
                              );
                              if (items.length === 0) {
                                setError(
                                  "No line items found in the PDF file."
                                );
                                return;
                              }
                              setInvoiceLineItems(
                                items.map((item) => ({
                                  description: item.description,
                                  amount: String(item.amount),
                                  type: item.type,
                                }))
                              );
                            } catch (e) {
                              setError(
                                `Failed to extract line items: ${e instanceof Error ? e.message : String(e)}`
                              );
                            }
                          }}
                        >
                          {isExtractingCharges
                            ? "Loading..."
                            : "Load line items from file"}
                        </Button>
                      )}
                  </div>
                </Col>
              )}
              {file.fileType == "statement" && (
                <>
                  <Col md="3">
                    <Form.Group
                      className="mb-3"
                      controlId="fileStatementNumber"
                    >
                      <Form.Label>Statement Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={file.statementNumber || ""}
                        onChange={(e) =>
                          setFile({
                            ...file,
                            statementNumber: e.target.value,
                          })
                        }
                        data-testid={`${file.fileName}-statement-number`}
                      />
                    </Form.Group>
                  </Col>
                  <Col md="3">
                    <Form.Group
                      className="mb-3"
                      controlId="fileStatementAmount"
                    >
                      <Form.Label>Statement Amount</Form.Label>
                      <Form.Control
                        type="text"
                        value={file.statementAmount || ""}
                        onChange={(e) =>
                          setFile({
                            ...file,
                            statementAmount: e.target.value,
                          })
                        }
                        data-testid={`${file.fileName}-statement-amount`}
                      />
                    </Form.Group>
                  </Col>
                  <Col md="3">
                    <Form.Group className="mb-3" controlId="fileIssueDate">
                      <Form.Label>Issue Date</Form.Label>
                      <Form.Control
                        type="date"
                        onClick={(e) => {
                          const element = e.target;
                          if ("showPicker" in HTMLInputElement.prototype) {
                            // showPicker() is supported. Chrome 99+ and Safari 16+
                            // see https://caniuse.com/?search=showPicker
                            (element as HTMLInputElement).showPicker();
                          }
                        }}
                        value={epochSecondsToDateInTz(
                          file.issueDate,
                          site.timeZone
                        )}
                        onChange={(e) => {
                          const date = new Date(e.target.value);
                          const invalidDate = isNaN(date as unknown as number);
                          const epochSeconds = invalidDate
                            ? null
                            : date.getTime() / 1000; // if the date was cleared, set to null, otherwise getTime returns milliseconds, but we want seconds
                          setFile({
                            ...file,
                            issueDate: epochSeconds,
                          });
                        }}
                        data-testid={`${file.fileName}-issue-date`}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
            <Row>
              <Col md="3">
                <Form.Group className="mb-3" controlId="fileAccess">
                  <Form.Label>File access</Form.Label>
                  <Form.Select
                    value={
                      file.isInternal
                        ? "__internal__"
                        : (file.siteOwnerships?.length ?? 0) > 0
                          ? file.siteOwnerships![0].orgID
                          : ""
                    }
                    onChange={(e) => {
                      if (e.target.value === "__internal__") {
                        setFile({
                          ...file,
                          isInternal: true,
                          siteOwnerships: [],
                        });
                      } else {
                        setFile({
                          ...file,
                          isInternal: false,
                          siteOwnerships:
                            e.target.value.length === 0
                              ? []
                              : site.siteOwnerships.filter(
                                  (so) => so.orgID === e.target.value
                                ),
                        });
                      }
                    }}
                    className="mb-3"
                  >
                    {site.siteOwnerships.map((ownership) => (
                      <option key={ownership.orgID} value={ownership.orgID}>
                        {ownership.name}
                      </option>
                    ))}
                    <option value="">Common</option>
                    <option value="__internal__">Internal</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="3">
                <Form.Group className="mb-3" controlId="filePublished">
                  <Form.Label>Published?</Form.Label>
                  <Form.Select
                    value={file.isPublished ? "true" : "false"}
                    onChange={(e) =>
                      setFile({
                        ...file,
                        isPublished: e.target.value === "true",
                      })
                    }
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          <Button
            variant="inrangesecondary"
            onClick={onClose}
            style={{ width: "200px" }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={() => {
              // TODO: Fix this handling so we don't need to strip siteID here
              let fileToUpload = {};
              if (file.fileVersionId) {
                // Update case - pass through all fields
                fileToUpload = file;
              } else {
                // Create case - omit siteID and user/time fields
                const {
                  siteID: _siteID,
                  createdBy: _createdBy,
                  createdAt: _createdAt,
                  updatedAt: _updatedAt,
                  ...fileWithoutVars
                } = file;
                fileToUpload = fileWithoutVars;
              }
              updateSite.mutate(
                {
                  site: {},
                  addedFiles: [fileToUpload],
                },
                {
                  onSettled: () => {
                    setSaveDisabled(false);
                  },
                  onSuccess: () => {
                    onClose();
                  },
                  onError: (error) => {
                    const axiosError = error as AxiosError;
                    const invalidStates =
                      axiosError.response?.data?.["invalid_states"] ?? [];
                    const fieldErrors: Record<string, string> = {};
                    invalidStates.forEach((error: any) => {
                      fieldErrors[
                        error.loc
                          .filter((elem: string) => "__root__" !== elem)
                          .join(".")
                      ] = error.msg;
                    });
                    const errorMessage =
                      axiosError.response?.data?.["message"] ||
                      axiosError.message;
                    const fieldErrorsStr =
                      Object.values(fieldErrors).length === 0
                        ? ""
                        : `; ${JSON.stringify(fieldErrors)}`;
                    setError(
                      `Error uploading file: ${errorMessage}${fieldErrorsStr}`
                    );
                  },
                }
              );
            }}
            style={{ width: "200px" }}
            disabled={saveDisabled || uploadInProgress}
          >
            {isExistingFile ? "Update" : "Add"} file
          </Button>
        </div>
      </ModalView>
    </Modal>
  );
};

export default EditFileModal;
